import { useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Header() {
  const { t } = useTranslation();
  const { lang = "hr" } = useParams();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const base = `/${lang}`;

  const NAV = [
    { key: "about", target: "about" },
    { key: "how", target: "how" },
    { key: "trust", target: "trust" },
    { key: "contact", target: "contact" },
  ];

  const scrollToSection = (id) => {
    // If not on the home page, navigate first then scroll after mount
    if (typeof window === "undefined") return;
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`${base}#${id}`);
      setTimeout(() => {
        const el2 = document.getElementById(id);
        if (el2) el2.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    }
  };

  return (
    <header
      data-nav="true"
      className="sticky top-0 z-40 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70 border-b border-border"
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-1.5 focus:text-primary-foreground"
      >
        {t("nav.skip_to_content")}
      </a>

      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <NavLink
            to={base}
            className="flex items-center text-foreground"
            aria-label="Arqovena — home"
            data-testid="header-logo"
          >
            <Logo className="h-8" />
          </NavLink>

          <nav
            aria-label="Primary"
            className="hidden md:flex items-center gap-6 text-[0.78rem] font-medium tracking-[0.08em] uppercase"
          >
            {NAV.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => scrollToSection(item.target)}
                className="text-muted-foreground hover:text-foreground transition-colors duration-150"
                data-testid={`nav-${item.key}-link`}
              >
                {t(`nav.${item.key}`)}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <LanguageSwitcher variant="header" />
            <Button
              type="button"
              size="sm"
              className="h-9 rounded-lg tracking-[0.06em]"
              onClick={() => scrollToSection("contact")}
              data-testid="header-primary-cta"
            >
              {t("nav.cta")}
            </Button>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher variant="header" />
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label={t("nav.open_menu")}
                  data-testid="mobile-menu-button"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-foreground"
                >
                  <Menu className="h-4 w-4" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] sm:w-[380px] bg-card">
                <SheetTitle className="sr-only">{t("nav.open_menu")}</SheetTitle>
                <div className="mt-2 flex items-center justify-between">
                  <Logo className="h-8" />
                  <button
                    type="button"
                    aria-label={t("nav.close_menu")}
                    onClick={() => setOpen(false)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <nav className="mt-8 flex flex-col gap-1" aria-label="Mobile primary">
                  {NAV.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        setTimeout(() => scrollToSection(item.target), 50);
                      }}
                      className="text-left rounded-md px-3 py-3 text-base tracking-[0.02em] hover:bg-muted transition-colors"
                      data-testid={`mobile-nav-${item.key}-link`}
                    >
                      {t(`nav.${item.key}`)}
                    </button>
                  ))}
                </nav>
                <div className="mt-6">
                  <Button
                    type="button"
                    className="w-full h-11 rounded-lg tracking-[0.06em]"
                    onClick={() => {
                      setOpen(false);
                      setTimeout(() => scrollToSection("contact"), 50);
                    }}
                    data-testid="mobile-primary-cta"
                  >
                    {t("nav.cta")}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
